<template>
  <div class="share-link-wrap">
  <v-tooltip bottom>
    <template #activator="{ on }">
      <v-btn
        :block="block"
        :outlined="!block"
        :depressed="block"
        :color="block ? 'primary' : 'white'"
        class="text-none"
        :class="block ? 'multi-line-button' : 'share-bar-btn'"
        data-test="share-copy"
        :loading="busy"
        :disabled="disabled || !articleId || revoking"
        v-on="on"
        @click="copyLink"
      >
        <v-icon left>
          {{ copied ? 'done' : 'link' }}
        </v-icon>
        {{ copied ? $t('share.copied') : $t('share.copy') }}
      </v-btn>
    </template>
    <span>{{ $t('share.tooltip') }}</span>
  </v-tooltip>
  <v-btn
    v-if="shared"
    :block="block"
    text
    small
    :color="block ? 'primary' : 'white'"
    class="text-none share-revoke-btn"
    data-test="share-revoke"
    :loading="revoking"
    :disabled="disabled || busy"
    @click="confirmRevoke = true"
  >
    <v-icon
      left
      small
    >
      link_off
    </v-icon>
    {{ $t('share.revoke') }}
  </v-btn>
  <v-dialog
    v-model="confirmRevoke"
    max-width="440"
  >
    <v-card>
      <v-card-title class="text-h6">
        {{ $t('share.revokeConfirmTitle') }}
      </v-card-title>
      <v-card-text>
        {{ $t('share.revokeConfirmBody') }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          class="text-none"
          data-test="share-revoke-cancel"
          @click="confirmRevoke = false"
        >
          {{ $t('share.revokeCancel') }}
        </v-btn>
        <v-btn
          depressed
          color="error"
          class="text-none"
          data-test="share-revoke-confirm"
          @click="revokeLink"
        >
          {{ $t('share.revokeConfirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {Article} from '@/model/Article';
    import {ensureShareLink, revokeShareLink, shareUrl} from '@/service/articleShare';
    import {Toast} from '@/model/Toast';
    import {captureException} from '@/plugins/Sentry';

    export default Vue.extend({
        name: 'ArticleShareButton',
        props: {
            block: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            busy: false,
            copied: false,
            revoking: false,
            confirmRevoke: false,
        }),
        computed: {
            article(): Article {
                return this.$store.getters.article;
            },
            articleId(): string {
                return this.article && this.article.id ? this.article.id : '';
            },
            shared(): boolean {
                return !!this.articleId && !!this.article.shareToken;
            }
        },
        methods: {
            copyLink(): void {
                if (this.busy || this.revoking || !this.articleId) {
                    return;
                }
                this.busy = true;
                ensureShareLink(this.article)
                    .then((token) => {
                        const url = shareUrl(token);
                        return navigator.clipboard.writeText(url).then(() => url);
                    })
                    .then(() => {
                        this.copied = true;
                        this.$store.dispatch('toasts/add', {
                            type: 'success',
                            title: 'share.copiedTitle',
                            content: 'share.copiedBody',
                        } as Toast);
                        setTimeout(() => {
                            this.copied = false;
                        }, 2500);
                    })
                    .catch((error) => {
                        captureException(error);
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'share.errorTitle',
                            content: 'share.errorBody',
                        } as Toast);
                    })
                    .finally(() => {
                        this.busy = false;
                    });
            },
            revokeLink(): void {
                this.confirmRevoke = false;
                if (this.revoking || !this.articleId) {
                    return;
                }
                this.revoking = true;
                revokeShareLink(this.article)
                    .then(() => {
                        this.copied = false;
                        this.$store.dispatch('toasts/add', {
                            type: 'success',
                            title: 'share.revokedTitle',
                            content: 'share.revokedBody',
                        } as Toast);
                    })
                    .catch((error) => {
                        captureException(error);
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'share.revokeErrorTitle',
                            content: 'share.revokeErrorBody',
                        } as Toast);
                    })
                    .finally(() => {
                        this.revoking = false;
                    });
            }
        }
    });
</script>

<style scoped lang="sass">
.share-bar-btn
    height: 44px !important
    min-width: 0 !important
    padding: 0 20px !important
    font-weight: 700 !important
    letter-spacing: 0.04em !important
    white-space: nowrap
    border: 2px solid #ffffff !important
    box-shadow: inset 0 0 0 1px #ffffff
    color: #fff !important
    background: transparent !important
    caret-color: #fff !important

    .v-icon
        color: #fff !important

.share-revoke-btn
    margin-top: 4px
</style>
